import { IMEvent, ErrorType, TriggerType } from '../consts.js';
import { Logger } from '../metrics/logger.js';
import { Subject } from './Subject.js';
import { BaseError } from '../errors/BaseError.js';
import { debounceWithArgs, throttleWithArgs, debounce, throttle } from '../utils/trigger.js';
import { ContextClassBase, ServiceIdentity } from '../service/RuntimeContext.js';

class EventBus extends ContextClassBase {
    constructor() {
        super(...arguments);
        this.subscriptions = new Map();
    }
    /**
     * 创建一个事件订阅
     * @typeParam T 回调数据类型
     * @param event 事件类型
     * @param func 事件回调函数
     * @returns 回调函数 Index
     */
    subscribe(event, func) {
        var _a;
        if (!(Object.values(IMEvent).includes(event))) {
            throw new BaseError({
                ctx: this.ctx,
                type: ErrorType.InvalidParam,
                msg: `unknown event: ${event}`,
                sender: this
            });
        }
        if (!this.subscriptions.has(event)) {
            this.subscriptions.set(event, new Subject(this.ctx));
        }
        const subject = this.subscriptions.get(event);
        const throttleLimit = typeof this.ctx.option.throttle === 'number' ? this.ctx.option.throttle : 100;
        const triggerStrategy = (_a = this.ctx.option.triggerStrategy) !== null && _a !== void 0 ? _a : {};
        if (this.ctx.option.throttle === false) {
            return subject.subscribe(func);
        }
        else {
            if (triggerStrategy[event] !== undefined) {
                Logger.ctxDebug(this.ctx, `apply strategy: ${TriggerType[triggerStrategy[event]]} to event: ${event}`);
                switch (triggerStrategy[event]) {
                    case TriggerType.Throttle:
                        return subject.subscribe(throttle(func, throttleLimit, throttleLimit));
                    case TriggerType.Debounce:
                        return subject.subscribe(debounce(func, throttleLimit));
                    case TriggerType.ThrottleWithArgs:
                        return subject.subscribe(throttleWithArgs(func, throttleLimit, throttleLimit));
                    case TriggerType.DebounceWithArgs:
                        return subject.subscribe(debounceWithArgs(func, throttleLimit));
                    case TriggerType.Immediate: // fall through to default
                    default:
                        return subject.subscribe(func);
                }
            }
            else {
                return subject.subscribe(func);
            }
        }
    }
    /**
     * 取消一个事件订阅
     * @param event 事件类型
     * @param handler 回调函数 Index
     */
    unsubscribe(event, handler) {
        if (!this.subscriptions.has(event)) {
            return;
        }
        this.subscriptions.get(event).unsubscribe(handler);
    }
    /** @internal */
    unsubscribeAll() {
        for (const sub of Object.values(this.subscriptions)) {
            sub.unsubscribeAll();
        }
        this.subscriptions.clear();
    }
    /** @internal */
    emitEmpty(event, sender) {
        return this.emit(event, sender, undefined);
    }
    /** @internal */
    emit(event, sender, e) {
        this.resolve(ServiceIdentity.Monitor).emitEvent(event, sender, e);
        const subject = this.subscriptions.get(event);
        if (subject) {
            if (e) {
                Logger.ctxDebug(this.ctx, `emit event "${event}" with sender:`, sender, ', eventArgs:', e);
            }
            else {
                Logger.ctxDebug(this.ctx, `emit event "${event}" with sender:`, sender);
            }
            subject.next(e, sender);
        }
    }
}

export { EventBus };
//# sourceMappingURL=EventBus.js.map
